import { useEffect } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import SeoHelmet from "./components/SeoHelmet";
import useAnalytics from "./hooks/useAnalytics";
import DiscountAdmin from "./pages/DiscountAdmin";
import { PricingProvider } from "./experience/PricingContext";
import Home, { Founder } from "./experience/Home";
import Directory from "./experience/Directory";
import ServiceDetail from "./experience/ServiceDetail";
import Payment from "./experience/Payment";
import Reviews from "./experience/Reviews";
import { Arrow, Header, Footer } from "./experience/Shared";
import { services, servicePath } from "./experience/catalog";
import "./experience/experience.css";
import "./experience/atmosphere.css";
import "./experience/journal/journal.css";
import "./experience/refinements.css";
import "./experience/celestial.css";
import Scene from "./experience/observatory/Scene";
import JourneyUX, { CertificateGallery } from "./experience/JourneyUX";
import "./experience/journey.css";
import "./experience/observatory/observatory.css";
import "./experience/observatory/daybreak.css";
import "./experience/observatory/mobile.css";
import ExperienceMotion from "./experience/ExperienceMotion";
import TouchAtmosphere from "./experience/observatory/TouchAtmosphere";
import { JournalIndex, JournalPost } from "./experience/journal/Journal";
import { articles, articlePath } from "./experience/journal/content";
import { pageMetadata } from "./seo/metadata";
import { NavratriRibbon } from "./experience/observatory/Navratri";

function PageEffects() {
  const { pathname, hash } = useLocation();
  useAnalytics();
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      target?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
    } else window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}
function PageMeta() {
  const { pathname } = useLocation();
  return <SeoHelmet {...pageMetadata(pathname)} />;
}

function Layout() {
  return (
    <>
      <PageMeta />
      <Header />
      <ExperienceMotion />
      <TouchAtmosphere />
      <main id="main" tabIndex={-1}>
        <NavratriRibbon />
        <Outlet />
      </main>
      <Footer />
      <JourneyUX />
    </>
  );
}
function AboutPage() {
  return (
    <>
      <Founder full />
      <section className="container certifications">
        <p className="eyebrow">CONTINUING THE PRACTICE</p>
        <h2>Learning & certifications.</h2>
        <CertificateGallery />
      </section>
      <Reviews page="Home" />
    </>
  );
}
function NotFound() {
  return (
    <section className="container empty-state observatory-detour">
      <div className="detour-scene"><Scene slug="past-life"/></div>
      <p className="eyebrow">404 · A SMALL DETOUR</p>
      <h1>Let’s find your way.</h1>
      <p>
        This page is not here. Your next step might be in our services, or you
        can return home.
      </p>
      <Link className="button" to="/services">
        Explore services <Arrow />
      </Link>
      <p>
        <Link className="text-link" to="/">
          Return home
        </Link>
      </p>
    </section>
  );
}
export function SiteRoutes() {
  return (
    <>
      <PricingProvider>
        <PageEffects />
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="journal" element={<JournalIndex />} />
            {articles.map(article => <Route key={article.slug} path={articlePath(article)} element={<JournalPost article={article} />} />)}
            <Route path="services" element={<Directory />} />
            <Route
              path="services/healing"
              element={<Directory kind="Healing" />}
            />
            <Route
              path="services/courses"
              element={<Directory kind="Courses" />}
            />
            {services.map((service) => (
              <Route
                key={service.slug}
                path={servicePath(service)}
                element={<ServiceDetail key={service.slug} service={service} />}
              />
            ))}
            <Route
              path="relationship-healing"
              element={<Navigate to="/services/relationship-healing" replace />}
            />
            <Route path="payment" element={<Payment />} />
            <Route
              path="client/discount-calendar"
              element={
                <div className="admin-shell">
                  <DiscountAdmin />
                </div>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </PricingProvider>
    </>
  );
}
export default function App() {
  return <BrowserRouter><SiteRoutes /></BrowserRouter>;
}
